import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getMyListings, createListing, updateListing, deleteListing, uploadListingImages, getCountries, getCities, getNeighborhoods, searchNeighborhoods, proposeNeighborhood, generateAIDescription, getListing, getUserStats } from '../lib/api';
import VideoUploader from '../components/VideoUploader';
import { uploadVideoToListing } from '../lib/videoUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, Home, Loader2, Upload, X, Sparkles, MessageCircle, Heart, TrendingUp, BarChart3, MapPin, AlertCircle, CheckCircle2, Circle } from 'lucide-react';

const DashboardPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="dashboard-title">
            {t('dashboard')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' ? `Bienvenue, ${user?.name}` : `Welcome, ${user?.name}`}
          </p>
        </div>

        <Routes>
          <Route index element={<MyListings />} />
          <Route path="new" element={<ListingForm />} />
          <Route path="edit/:id" element={<ListingForm />} />
        </Routes>
      </div>
    </div>
  );
};

const ProfileCompletion = ({ user, listings, language }) => {
  const checks = [
    {
      done: !!user?.phone,
      labelFr: 'Numéro de téléphone renseigné',
      labelEn: 'Phone number filled in',
      hintFr: 'Ajoutez votre téléphone pour être joignable',
      hintEn: 'Add your phone so guests can reach you',
    },
    {
      done: !!user?.picture,
      labelFr: 'Photo de profil',
      labelEn: 'Profile picture',
      hintFr: 'Une photo augmente la confiance des voyageurs',
      hintEn: 'A picture boosts traveller trust',
    },
    {
      done: (listings?.length || 0) >= 1,
      labelFr: 'Au moins une annonce publiée',
      labelEn: 'At least one published listing',
      hintFr: 'Créez votre première annonce',
      hintEn: 'Create your first listing',
    },
    {
      done: (listings || []).some((l) => l.status === 'approved'),
      labelFr: 'Une annonce approuvée',
      labelEn: 'One approved listing',
      hintFr: 'En attente de validation par l\u2019équipe',
      hintEn: 'Pending review by our team',
    },
    {
      done: (listings || []).some((l) => (l.images?.length || 0) >= 3),
      labelFr: 'Au moins 3 photos sur une annonce',
      labelEn: 'At least 3 photos on a listing',
      hintFr: 'Plus de photos = plus de réservations',
      hintEn: 'More photos = more bookings',
    },
    {
      done: (listings || []).some((l) => !!l.video_url),
      labelFr: 'Vidéo ajoutée à une annonce',
      labelEn: 'Video added to a listing',
      hintFr: 'Une vidéo multiplie les vues par 3',
      hintEn: 'A video can triple your views',
    },
  ];

  const completed = checks.filter((c) => c.done).length;
  const total = checks.length;
  const percent = Math.round((completed / total) * 100);
  const nextStep = checks.find((c) => !c.done);

  let barColor = 'bg-amber-500';
  if (percent >= 80) barColor = 'bg-emerald-500';
  else if (percent >= 50) barColor = 'bg-blue-500';

  if (percent === 100) {
    return (
      <Card className="mb-6 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="profile-completion-card">
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-900">
              {language === 'fr' ? 'Profil 100 % complet 🎉' : 'Profile 100% complete 🎉'}
            </p>
            <p className="text-sm text-emerald-700/80">
              {language === 'fr'
                ? 'Votre profil maximise vos chances de réservation.'
                : 'Your profile is fully optimised for bookings.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid="profile-completion-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="font-semibold">
              {language === 'fr' ? 'Complétion du profil' : 'Profile completion'}
            </p>
            <p className="text-xs text-muted-foreground">
              {completed}/{total} {language === 'fr' ? 'étapes complétées' : 'steps completed'}
            </p>
          </div>
          <span className="text-2xl font-bold tabular-nums" data-testid="profile-completion-percent">
            {percent}%
          </span>
        </div>

        <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
          <div
            className={`h-full ${barColor} transition-all duration-500 ease-out`}
            style={{ width: `${percent}%` }}
          />
        </div>

        {nextStep && (
          <div className="flex items-start gap-2 p-3 bg-muted/40 rounded-md">
            <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">
                {language === 'fr' ? 'Prochaine étape : ' : 'Next step: '}
                <span className="font-normal">
                  {language === 'fr' ? nextStep.labelFr : nextStep.labelEn}
                </span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {language === 'fr' ? nextStep.hintFr : nextStep.hintEn}
              </p>
            </div>
          </div>
        )}

        <ul className="mt-3 space-y-1.5">
          {checks.map((c) => (
            <li key={c.labelEn} className="flex items-center gap-2 text-sm">
              {c.done ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className={c.done ? 'text-foreground' : 'text-muted-foreground'}>
                {language === 'fr' ? c.labelFr : c.labelEn}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const MyListings = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [listingsRes, statsRes] = await Promise.all([
        getMyListings(),
        getUserStats()
      ]);
      setListings(listingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm(language === 'fr' ? 'Voulez-vous vraiment supprimer cette annonce ?' : 'Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await deleteListing(listingId);
      setListings(prev => prev.filter(l => l.listing_id !== listingId));
      toast.success(t('listingDeleted'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleToggleActive = async (listing) => {
    // Owner can only toggle visibility on APPROVED listings
    if (listing.status !== 'approved') {
      toast.error(language === 'fr'
        ? 'Seul le super admin peut activer cette annonce. Elle est en attente de validation.'
        : 'Only the super admin can activate this listing. It is pending validation.');
      return;
    }
    try {
      await updateListing(listing.listing_id, { is_active: !listing.is_active });
      setListings(prev => prev.map(l => 
        l.listing_id === listing.listing_id 
          ? { ...l, is_active: !l.is_active }
          : l
      ));
      toast.success(t('listingUpdated'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const typeLabels = {
    apartment: t('apartment'),
    room: t('room'),
    hotel: t('hotel'),
    studio: t('studio'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Profile completion progress */}
      <ProfileCompletion user={user} listings={listings} language={language} />

      {/* User Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Home className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Annonces' : 'Listings'}</p>
                  <p className="text-xl font-bold">{stats.listings?.total || 0}</p>
                  <p className="text-xs text-green-600">{stats.listings?.active || 0} {language === 'fr' ? 'actives' : 'active'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Vues totales' : 'Total views'}</p>
                  <p className="text-xl font-bold">{stats.performance?.total_views?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Contacts WhatsApp' : 'WhatsApp contacts'}</p>
                  <p className="text-xl font-bold">{stats.performance?.total_whatsapp_clicks || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Favoris' : 'Favorites'}</p>
                  <p className="text-xl font-bold">{stats.performance?.total_favorites || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Performing Listings */}
      {stats?.top_listings?.by_views?.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              {language === 'fr' ? 'Vos annonces les plus vues' : 'Your top viewed listings'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.top_listings.by_views.slice(0, 3).map((item, index) => (
                <div key={item.listing_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{index + 1}</span>
                    <span className="font-medium truncate max-w-[200px]">{item.title}</span>
                  </div>
                  <Badge variant="secondary">{item.views} {language === 'fr' ? 'vues' : 'views'}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold">{t('myListings')}</h2>
        <Link to="/dashboard/new">
          <Button className="gap-2" data-testid="add-listing-btn">
            <Plus className="h-4 w-4" />
            {t('addListing')}
          </Button>
        </Link>
      </div>

      {listings.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              {language === 'fr' ? 'Vous n\'avez pas encore d\'annonces' : 'You don\'t have any listings yet'}
            </p>
            <Link to="/dashboard/new">
              <Button>{t('addListing')}</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="listings-list">
          {listings.map((listing) => (
            <Card key={listing.listing_id} data-testid={`my-listing-${listing.listing_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Image */}
                  <div className="w-full md:w-48 aspect-video md:aspect-[4/3] rounded-lg overflow-hidden bg-muted shrink-0">
                    {listing.images?.[0] ? (
                      <img 
                        src={listing.images[0]} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold line-clamp-1">{listing.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {listing.neighborhood}, {listing.city}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {listing.status === 'pending' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300" data-testid={`status-pending-${listing.listing_id}`}>
                            {language === 'fr' ? 'En attente de validation' : 'Pending validation'}
                          </Badge>
                        )}
                        {listing.status === 'rejected' && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300" data-testid={`status-rejected-${listing.listing_id}`}>
                            {language === 'fr' ? 'Rejetée' : 'Rejected'}
                          </Badge>
                        )}
                        {listing.status === 'modification_requested' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300" data-testid={`status-mod-${listing.listing_id}`}>
                            {language === 'fr' ? 'Modification demandée' : 'Modification requested'}
                          </Badge>
                        )}
                        {listing.status === 'approved' && (
                          <Badge variant={listing.is_active ? "default" : "secondary"}>
                            {listing.is_active ? t('active') : t('inactive')}
                          </Badge>
                        )}
                        <Badge variant="outline">
                          {typeLabels[listing.listing_type]}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        {listing.price_per_night?.toLocaleString()} FCFA{t('perNight')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {listing.views} {t('views')}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={listing.is_active}
                          onCheckedChange={() => handleToggleActive(listing)}
                          disabled={listing.status !== 'approved'}
                          data-testid={`toggle-active-${listing.listing_id}`}
                        />
                        <span className="text-sm">
                          {listing.status !== 'approved'
                            ? (language === 'fr' ? 'Validation requise' : 'Validation required')
                            : (listing.is_active ? t('active') : t('inactive'))}
                        </span>
                      </div>
                      <div className="flex-1" />
                      <Link to={`/listings/${listing.listing_id}`}>
                        <Button variant="outline" size="sm" className="gap-1">
                          <Eye className="h-4 w-4" />
                          Voir
                        </Button>
                      </Link>
                      <Link to={`/dashboard/edit/${listing.listing_id}`}>
                        <Button variant="outline" size="sm" className="gap-1">
                          <Edit className="h-4 w-4" />
                          {t('editListing')}
                        </Button>
                      </Link>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => handleDelete(listing.listing_id)}
                        data-testid={`delete-${listing.listing_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('deleteListing')}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const ListingForm = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { id: listingId } = useParams(); // Get listing ID from URL params
  const isEditMode = !!listingId;
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideo, setExistingVideo] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    listing_type: 'apartment',
    country: 'Cameroun',
    city: '',
    neighborhood: '',
    location_precision: '',
    price_per_night: '',
    description: '',
    whatsapp: '',
    amenities: [],
    rules: '',
    is_furnished: true,
    has_wifi: false,
    has_ac: false,
    has_parking: false,
    max_guests: 2,
    google_maps_url: '',
  });

  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [neighborhoodQuery, setNeighborhoodQuery] = useState('');
  const [showNeighborhoodList, setShowNeighborhoodList] = useState(false);
  const [proposingNeighborhood, setProposingNeighborhood] = useState(false);

  // Load existing listing data in edit mode
  useEffect(() => {
    if (isEditMode && listingId) {
      loadListingData();
    }
  }, [listingId, isEditMode]);

  const loadListingData = async () => {
    try {
      setLoadingData(true);
      const res = await getListing(listingId);
      const listing = res.data;
      
      setFormData({
        title: listing.title || '',
        listing_type: listing.listing_type || 'apartment',
        country: listing.country || 'Cameroun',
        city: listing.city || '',
        neighborhood: listing.neighborhood || '',
        location_precision: listing.location_precision || '',
        price_per_night: listing.price_per_night?.toString() || '',
        description: listing.description || '',
        whatsapp: listing.whatsapp || '',
        amenities: listing.amenities || [],
        rules: listing.rules || '',
        is_furnished: listing.is_furnished ?? true,
        has_wifi: listing.has_wifi ?? false,
        has_ac: listing.has_ac ?? false,
        has_parking: listing.has_parking ?? false,
        max_guests: listing.max_guests || 2,
        google_maps_url: listing.google_maps_url || '',
      });
      setNeighborhoodQuery(listing.neighborhood || '');
      
      setExistingImages(listing.images || []);
      setExistingVideo(listing.video_url ? {
        url: listing.video_url,
        thumbnail: listing.video_thumbnail,
        duration: listing.video_duration,
      } : null);
    } catch (error) {
      console.error('Error loading listing:', error);
      toast.error('Erreur lors du chargement de l\'annonce');
      navigate('/dashboard');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (selectedCountryId) {
      loadCities(selectedCountryId);
    }
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedCityId) {
      loadNeighborhoods(selectedCityId);
    }
  }, [selectedCityId]);

  // In edit mode: once cities are loaded and we have city name, set selectedCityId
  useEffect(() => {
    if (isEditMode && cities.length > 0 && formData.city && !selectedCityId) {
      const matchingCity = cities.find(c => c.name.toLowerCase() === formData.city.toLowerCase());
      if (matchingCity) {
        setSelectedCityId(matchingCity.city_id);
      }
    }
  }, [cities, formData.city, isEditMode, selectedCityId]);

  const loadCountries = async () => {
    try {
      const res = await getCountries();
      setCountries(res.data);
      if (res.data.length > 0 && !selectedCountryId) {
        setSelectedCountryId(res.data[0].country_id);
        if (!isEditMode) {
          setFormData(prev => ({ ...prev, country: res.data[0].name }));
        }
      }
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadCities = async (countryId) => {
    try {
      const res = await getCities(countryId);
      setCities(res.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadNeighborhoods = async (cityId) => {
    try {
      const res = await getNeighborhoods(cityId);
      setNeighborhoods(res.data);
    } catch (error) {
      console.error('Error loading neighborhoods:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCityChange = (cityId) => {
    const city = cities.find(c => c.city_id === cityId);
    if (city) {
      setSelectedCityId(cityId);
      setFormData(prev => ({ ...prev, city: city.name, neighborhood: '' }));
      setNeighborhoodQuery('');
    }
  };

  const handleNeighborhoodSelect = (name) => {
    setFormData(prev => ({ ...prev, neighborhood: name }));
    setNeighborhoodQuery(name);
    setShowNeighborhoodList(false);
  };

  const handleProposeNeighborhood = async () => {
    if (!selectedCityId) {
      toast.error(language === 'fr' ? 'Veuillez d\'abord sélectionner une ville' : 'Please select a city first');
      return;
    }
    const proposedName = neighborhoodQuery.trim();
    if (!proposedName || proposedName.length < 2) {
      toast.error(language === 'fr' ? 'Le nom du quartier doit contenir au moins 2 caractères' : 'Neighborhood name must be at least 2 characters');
      return;
    }
    setProposingNeighborhood(true);
    try {
      const res = await proposeNeighborhood({
        name: proposedName,
        city_id: selectedCityId,
      });
      // Add the proposed neighborhood to local state so submit accepts it
      const newNeighborhood = {
        neighborhood_id: res.data.neighborhood_id,
        name: res.data.name || proposedName,
        city_id: selectedCityId,
        status: 'pending',
      };
      setNeighborhoods(prev => [...prev, newNeighborhood]);
      setFormData(prev => ({ ...prev, neighborhood: newNeighborhood.name }));
      setNeighborhoodQuery(newNeighborhood.name);
      setShowNeighborhoodList(false);
      toast.success(language === 'fr'
        ? 'Quartier proposé. Vous pouvez maintenant publier votre annonce — elle sera validée par un administrateur.'
        : 'Neighborhood proposed. You can now publish your listing — it will be validated by an admin.');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la proposition' : 'Error proposing neighborhood'));
    } finally {
      setProposingNeighborhood(false);
    }
  };

  const validateWhatsApp = (value) => {
    if (!value || !value.trim()) return false;
    const cleaned = value.replace(/[\s\-\.\(\)]/g, '');
    return /^\+?[0-9]{9,15}$/.test(cleaned);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > 10) {
      toast.error('Maximum 10 photos autorisées');
      return;
    }
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateAIDescription = async () => {
    if (!formData.city || !formData.neighborhood || !formData.price_per_night) {
      toast.error('Veuillez remplir la ville, le quartier et le prix d\'abord');
      return;
    }

    setGeneratingAI(true);
    try {
      const res = await generateAIDescription({
        title: formData.title || null,
        listing_type: formData.listing_type,
        city: formData.city,
        neighborhood: formData.neighborhood,
        price_per_night: parseFloat(formData.price_per_night),
        amenities: formData.amenities,
        max_guests: parseInt(formData.max_guests) || 2,
        is_furnished: formData.is_furnished,
        has_wifi: formData.has_wifi,
        has_ac: formData.has_ac,
        has_parking: formData.has_parking,
      });

      setFormData(prev => ({
        ...prev,
        description: res.data.description,
        title: res.data.suggested_title || prev.title,
      }));
      toast.success('Description générée par IA !');
    } catch (error) {
      console.error('Error generating AI description:', error);
      toast.error('Erreur lors de la génération de la description');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate City selected
    if (!formData.city) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une ville' : 'Please select a city');
      return;
    }

    // Validate Neighborhood is from approved list
    const validNeighborhood = neighborhoods.find(
      n => n.name.toLowerCase() === formData.neighborhood.trim().toLowerCase()
    );
    if (!validNeighborhood) {
      toast.error(language === 'fr'
        ? 'Veuillez sélectionner un quartier dans la liste ou en proposer un nouveau'
        : 'Please select a neighborhood from the list or propose a new one');
      return;
    }

    // Validate WhatsApp
    if (!validateWhatsApp(formData.whatsapp)) {
      toast.error(language === 'fr'
        ? 'Numéro WhatsApp invalide. Format attendu: +237XXXXXXXXX'
        : 'Invalid WhatsApp number. Expected format: +237XXXXXXXXX');
      return;
    }

    setLoading(true);

    try {
      const listingData = {
        ...formData,
        neighborhood: validNeighborhood.name,
        location_precision: formData.location_precision?.trim() || null,
        price_per_night: parseFloat(formData.price_per_night),
        max_guests: parseInt(formData.max_guests),
        google_maps_url: formData.google_maps_url || null,
      };

      if (isEditMode) {
        // Update existing listing
        await updateListing(listingId, listingData);
        
        // Upload new images if any
        if (selectedFiles.length > 0) {
          await uploadListingImages(listingId, selectedFiles);
        }
        
        toast.success(t('listingUpdated'));
      } else {
        // Create new listing
        const res = await createListing(listingData);
        const newListingId = res.data.listing_id;

        // Upload images if any
        if (selectedFiles.length > 0) {
          await uploadListingImages(newListingId, selectedFiles);
        }

        // Upload pending video if any (Cloudinary direct upload)
        if (pendingVideoFile) {
          try {
            await uploadVideoToListing(pendingVideoFile, newListingId, undefined, language);
          } catch (videoErr) {
            // Listing was created — surface video error without aborting
            toast.error(
              (language === 'fr' ? 'Annonce créée, mais erreur lors de l\'envoi de la vidéo : ' : 'Listing created, but video upload failed: ')
              + (videoErr?.message || '')
            );
          }
        }

        toast.success(language === 'fr'
          ? 'Annonce créée. En attente de validation par un administrateur.'
          : 'Listing created. Pending admin validation.');
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving listing:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('error'));
    } finally {
      setLoading(false);
    }
  };

  const amenitiesList = ['WiFi', 'Climatisation', 'Parking', 'Gardien', 'Piscine', 'Restaurant', 'Cuisine équipée', 'Machine à laver'];

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Show loading state while fetching existing data in edit mode
  if (loadingData) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Chargement des données...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEditMode ? 'Modifier l\'annonce' : t('addListing')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre de l'annonce *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Appartement Meublé à Bastos"
                required
                data-testid="listing-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="listing_type">Type de logement *</Label>
              <Select
                value={formData.listing_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, listing_type: v }))}
              >
                <SelectTrigger data-testid="listing-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">{t('apartment')}</SelectItem>
                  <SelectItem value="studio">{t('studio')}</SelectItem>
                  <SelectItem value="room">{t('room')}</SelectItem>
                  <SelectItem value="hotel">{t('hotel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Pays *</Label>
              <Select
                value={selectedCountryId}
                onValueChange={setSelectedCountryId}
              >
                <SelectTrigger data-testid="country-select">
                  <SelectValue placeholder="Sélectionner un pays" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.country_id} value={country.country_id}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city-select">Ville *</Label>
              <Select
                value={selectedCityId}
                onValueChange={handleCityChange}
              >
                <SelectTrigger id="city-select" data-testid="city-select">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner une ville' : 'Select a city'} />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.city_id} value={city.city_id} data-testid={`city-option-${city.name}`}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 relative">
              <Label htmlFor="neighborhood">Quartier *</Label>
              <Input
                id="neighborhood"
                name="neighborhood"
                value={neighborhoodQuery}
                onChange={(e) => {
                  setNeighborhoodQuery(e.target.value);
                  setFormData(prev => ({ ...prev, neighborhood: e.target.value }));
                  setShowNeighborhoodList(true);
                }}
                onFocus={() => setShowNeighborhoodList(true)}
                onBlur={() => setTimeout(() => setShowNeighborhoodList(false), 200)}
                placeholder={selectedCityId ? (language === 'fr' ? 'Tapez ou sélectionnez...' : 'Type or select...') : (language === 'fr' ? 'Choisir une ville d\'abord' : 'Select a city first')}
                disabled={!selectedCityId}
                autoComplete="off"
                required
                data-testid="neighborhood-input"
              />
              {showNeighborhoodList && selectedCityId && (
                <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="neighborhood-list">
                  {neighborhoods
                    .filter(n => !neighborhoodQuery || n.name.toLowerCase().includes(neighborhoodQuery.toLowerCase()))
                    .slice(0, 30)
                    .map((n) => (
                      <button
                        key={n.neighborhood_id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                        onMouseDown={(e) => { e.preventDefault(); handleNeighborhoodSelect(n.name); }}
                        data-testid={`neighborhood-option-${n.name}`}
                      >
                        {n.name}
                      </button>
                    ))}
                  {neighborhoodQuery.trim().length >= 2 &&
                    !neighborhoods.some(n => n.name.toLowerCase() === neighborhoodQuery.trim().toLowerCase()) && (
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-accent border-t flex items-center gap-2"
                        onMouseDown={(e) => { e.preventDefault(); handleProposeNeighborhood(); }}
                        disabled={proposingNeighborhood}
                        data-testid="propose-neighborhood-btn"
                      >
                        {proposingNeighborhood ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        {language === 'fr' ? `Proposer "${neighborhoodQuery.trim()}"` : `Propose "${neighborhoodQuery.trim()}"`}
                      </button>
                    )}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? 'Sélectionnez ou proposez un nouveau quartier' : 'Select or propose a new neighborhood'}
              </p>
            </div>
          </div>

          {/* Location precision (optional) */}
          <div className="space-y-2">
            <Label htmlFor="location_precision">
              {language === 'fr' ? 'Précision de localisation (optionnel)' : 'Location precision (optional)'}
            </Label>
            <Input
              id="location_precision"
              name="location_precision"
              value={formData.location_precision}
              onChange={handleChange}
              placeholder={language === 'fr' ? 'Ex: Carrefour Jouvence, près du marché central' : 'Ex: Near the main square'}
              data-testid="location-precision-input"
            />
          </div>

          {/* Price & Guests */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price_per_night">Prix par nuit (FCFA) *</Label>
              <Input
                id="price_per_night"
                name="price_per_night"
                type="number"
                value={formData.price_per_night}
                onChange={handleChange}
                placeholder="25000"
                required
                data-testid="listing-price-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_guests">Nombre de voyageurs max *</Label>
              <Input
                id="max_guests"
                name="max_guests"
                type="number"
                value={formData.max_guests}
                onChange={handleChange}
                min={1}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp">WhatsApp *</Label>
              <Input
                id="whatsapp"
                name="whatsapp"
                type="tel"
                value={formData.whatsapp}
                onChange={handleChange}
                placeholder="+237 6XX XXX XXX"
                required
                pattern="^\+?[0-9\s\-\.\(\)]{9,20}$"
                data-testid="listing-whatsapp-input"
              />
              {formData.whatsapp && !validateWhatsApp(formData.whatsapp) && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {language === 'fr' ? 'Format invalide (ex: +237682329126)' : 'Invalid format (ex: +237682329126)'}
                </p>
              )}
            </div>
          </div>

          {/* Google Maps Location (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="google_maps_url">Lien Google Maps (optionnel)</Label>
            <Input
              id="google_maps_url"
              name="google_maps_url"
              value={formData.google_maps_url}
              onChange={handleChange}
              placeholder="https://maps.google.com/..."
              data-testid="listing-google-maps-input"
            />
            <p className="text-xs text-muted-foreground">
              Ajoutez un lien Google Maps pour faciliter la localisation de votre logement
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="description">Description *</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateAIDescription}
                disabled={generatingAI}
                className="gap-2"
                data-testid="generate-ai-description-btn"
              >
                {generatingAI ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Générer avec l'IA
              </Button>
            </div>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez votre logement ou cliquez sur 'Générer avec l'IA'..."
              rows={5}
              required
              data-testid="listing-description-input"
            />
          </div>

          {/* Amenities */}
          <div className="space-y-4">
            <Label>Équipements</Label>
            <div className="flex flex-wrap gap-2">
              {amenitiesList.map((amenity) => (
                <Badge
                  key={amenity}
                  variant={formData.amenities.includes(amenity) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleAmenity(amenity)}
                >
                  {amenity}
                </Badge>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <Label>Caractéristiques</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_furnished"
                  checked={formData.is_furnished}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_furnished: v }))}
                />
                <Label htmlFor="is_furnished">{t('furnished')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_wifi"
                  checked={formData.has_wifi}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, has_wifi: v }))}
                />
                <Label htmlFor="has_wifi">{t('wifi')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_ac"
                  checked={formData.has_ac}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, has_ac: v }))}
                />
                <Label htmlFor="has_ac">{t('ac')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_parking"
                  checked={formData.has_parking}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, has_parking: v }))}
                />
                <Label htmlFor="has_parking">{t('parking')}</Label>
              </div>
            </div>
          </div>

          {/* Rules */}
          <div className="space-y-2">
            <Label htmlFor="rules">Règles (optionnel)</Label>
            <Textarea
              id="rules"
              name="rules"
              value={formData.rules}
              onChange={handleChange}
              placeholder="Ex: Non-fumeur, pas d'animaux..."
              rows={2}
            />
          </div>

          {/* Existing Images (Edit Mode) */}
          {isEditMode && existingImages.length > 0 && (
            <div className="space-y-4">
              <Label>Photos existantes</Label>
              <div className="flex flex-wrap gap-4">
                {existingImages.map((url, index) => (
                  <div key={url} className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={url}
                      alt={`Existing ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{existingImages.length} photos actuelles</p>
            </div>
          )}

          {/* New Images */}
          <div className="space-y-4">
            <Label>{isEditMode ? 'Ajouter des photos' : 'Photos (max 10)'}</Label>
            <div className="flex flex-wrap gap-4">
              {selectedFiles.map((file, index) => (
                <div key={`${file.name}-${file.size}-${file.lastModified}`} className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <label className={`w-24 h-24 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors ${selectedFiles.length >= 10 ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground mt-1">Ajouter</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={selectedFiles.length >= 10}
                />
              </label>
            </div>
            <p className="text-xs text-muted-foreground">{selectedFiles.length}/10 nouvelles photos</p>
          </div>

          {/* Video upload — Cloudinary (works in create + edit modes) */}
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Vidéo de présentation (optionnel)' : 'Presentation video (optional)'}</Label>
            <VideoUploader
              listingId={isEditMode ? listingId : null}
              currentVideoUrl={existingVideo?.url}
              currentVideoThumbnail={existingVideo?.thumbnail}
              currentVideoDuration={existingVideo?.duration}
              onChange={(updated) => setExistingVideo(updated?.video_url ? {
                url: updated.video_url,
                thumbnail: updated.video_thumbnail,
                duration: updated.video_duration,
              } : null)}
              pendingFile={pendingVideoFile}
              onPendingFileChange={setPendingVideoFile}
            />
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-listing-btn">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditMode ? 'Enregistrer les modifications' : 'Publier l\'annonce'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default DashboardPage;
